import { create } from 'zustand';
import type { EditorField } from '../../editor/editorStore';
import type { InputType, UploadResult } from '../../lib/types';
import {
  DEFAULT_SUBJECT,
  MAX_DOCUMENTS,
  clampToPage,
  newField,
  titleFromFilename,
  type QuickSendDoc,
  type QuickSendDraft,
} from '../../lib/quickSend';

/**
 * Quick send's working state — the documents being prepared, their fields, and
 * the recipient. Nothing persists: the page warns before leaving and resets on
 * unmount (uploads expire server-side on their own).
 */
interface QuickSendState extends QuickSendDraft {
  activeId: string | null;
  page: number;
  zoom: number;
  selectedUid: string | null;

  /** Adds up to the document cap; returns the docs actually added (to upload). */
  addDocs: (files: File[]) => QuickSendDoc[];
  uploaded: (id: string, res: UploadResult) => void;
  uploadFailed: (id: string, message: string) => void;
  removeDoc: (id: string) => void;
  moveDoc: (id: string, delta: -1 | 1) => void;
  renameDoc: (id: string, title: string) => void;
  openDoc: (id: string) => void;
  setPage: (page: number) => void;
  setZoom: (zoom: number) => void;
  addField: (type: InputType) => void;
  updateField: (uid: string, patch: Partial<EditorField>) => void;
  removeField: (uid: string) => void;
  select: (uid: string | null) => void;
  setForm: (patch: Partial<Pick<QuickSendDraft, 'name' | 'email' | 'subject' | 'message' | 'title'>>) => void;
  reset: () => void;
}

const initial = () => ({
  docs: [] as QuickSendDoc[],
  name: '',
  email: '',
  subject: DEFAULT_SUBJECT,
  message: '',
  title: '',
  activeId: null as string | null,
  page: 1,
  zoom: 1,
  selectedUid: null as string | null,
});

const mapDoc = (docs: QuickSendDoc[], id: string, fn: (d: QuickSendDoc) => QuickSendDoc) =>
  docs.map((d) => (d.id === id ? fn(d) : d));

export const useQuickSend = create<QuickSendState>((set, get) => ({
  ...initial(),

  addDocs: (files) => {
    const room = MAX_DOCUMENTS - get().docs.length;
    const added: QuickSendDoc[] = files.slice(0, Math.max(0, room)).map((file) => ({
      id: crypto.randomUUID(),
      file,
      title: titleFromFilename(file.name),
      state: 'uploading',
      pageSizes: [],
      fields: [],
    }));
    if (!added.length) return [];
    set((s) => ({
      docs: [...s.docs, ...added],
      // Open the first new document unless one is already being worked on.
      ...(s.activeId ? {} : { activeId: added[0].id, page: 1, selectedUid: null }),
    }));
    return added;
  },

  // A doc removed while its upload was in flight is simply not found here.
  uploaded: (id, res) =>
    set((s) => ({
      docs: mapDoc(s.docs, id, (d) => ({ ...d, state: 'ready', uploadId: res.uploadId, pageSizes: res.pageSizes })),
    })),
  uploadFailed: (id, message) =>
    set((s) => ({ docs: mapDoc(s.docs, id, (d) => ({ ...d, state: 'failed', error: message })) })),

  removeDoc: (id) =>
    set((s) => {
      const docs = s.docs.filter((d) => d.id !== id);
      if (s.activeId !== id) return { docs };
      return { docs, activeId: docs[0]?.id ?? null, page: 1, selectedUid: null };
    }),
  moveDoc: (id, delta) =>
    set((s) => {
      const i = s.docs.findIndex((d) => d.id === id);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= s.docs.length) return {};
      const docs = s.docs.slice();
      [docs[i], docs[j]] = [docs[j], docs[i]];
      return { docs };
    }),
  renameDoc: (id, title) => set((s) => ({ docs: mapDoc(s.docs, id, (d) => ({ ...d, title })) })),
  openDoc: (id) => set((s) => (s.activeId === id ? {} : { activeId: id, page: 1, selectedUid: null })),
  setPage: (page) => set({ page, selectedUid: null }),
  setZoom: (zoom) => set({ zoom }),

  addField: (type) => {
    const { docs, activeId, page } = get();
    const doc = docs.find((d) => d.id === activeId);
    const size = doc?.pageSizes[page - 1];
    if (!doc || !size) return;
    const onPage = doc.fields.filter((f) => f.page === page).length;
    const field = newField(type, page, size, onPage, crypto.randomUUID());
    set((s) => ({
      docs: mapDoc(s.docs, doc.id, (d) => ({ ...d, fields: [...d.fields, field] })),
      selectedUid: field.uid,
    }));
  },

  // Every edit lands back on the page — dragging can't leave it, but the
  // number inputs and a page change can.
  updateField: (uid, patch) =>
    set((s) => ({
      docs: s.docs.map((d) =>
        d.fields.some((f) => f.uid === uid)
          ? {
              ...d,
              fields: d.fields.map((f) => {
                if (f.uid !== uid) return f;
                const next = { ...f, ...patch };
                const size = d.pageSizes[next.page - 1];
                return size ? clampToPage(next, size) : next;
              }),
            }
          : d,
      ),
    })),
  removeField: (uid) =>
    set((s) => ({
      docs: s.docs.map((d) => ({ ...d, fields: d.fields.filter((f) => f.uid !== uid) })),
      selectedUid: s.selectedUid === uid ? null : s.selectedUid,
    })),
  select: (uid) => set({ selectedUid: uid }),
  setForm: (patch) => set(patch),
  reset: () => set(initial()),
}));
